import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Button, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Snackbar, Alert, Divider, Tooltip, Stack, Fade, CircularProgress
} from '@mui/material';
import {
  Zap, Crown, Star, Shield, Users, Check, X,
  TrendingUp, ReceiptText, Layers, Sparkles,
  CreditCard, RefreshCw, XCircle, ChevronRight,
  AlertTriangle, Infinity as InfinityIcon, CheckCircle2, Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// ─── Plan metadata ────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '€0',
    period: 'forever',
    color: '#64748b',
    gradient: 'linear-gradient(135deg, #334155 0%, #64748b 100%)',
    icon: Shield,
    description: 'Core budgeting for individuals',
    features: [
      '50 transactions / month',
      '5 budget categories',
      '3 receipts / month',
      'Mobile app',
    ],
    limits: { transactions: 50, budgetCategories: 5, receipts: 3, splitBills: 0 },
  },
  {
    id: 'standard',
    name: 'Standard',
    price: '€2.99',
    period: '/month',
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
    icon: Star,
    description: 'More power for serious budgeters',
    features: [
      '200 transactions / month',
      '15 budget categories',
      '50 receipts / month',
      '10 split bills',
      'Data export (CSV)',
      'Custom reports',
      'Email support',
    ],
    limits: { transactions: 200, budgetCategories: 15, receipts: 50, splitBills: 10 },
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '€4.99',
    period: '/month',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
    icon: Crown,
    highlighted: true,
    description: 'Unlimited + AI-powered insights',
    features: [
      'Unlimited transactions',
      'Unlimited categories',
      'AI insights & tips',
      '200 receipts / month',
      'Advanced analytics',
      'Priority support',
      'Export CSV / Excel',
    ],
    limits: { transactions: -1, budgetCategories: -1, receipts: 200, splitBills: -1 },
  },
  {
    id: 'family',
    name: 'Family',
    price: '€9.99',
    period: '/month',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    icon: Users,
    description: 'Everything for your household',
    features: [
      'Everything in Premium',
      'Up to 5 accounts',
      '500 receipts / month',
      'Shared budgets',
      'Family dashboard',
      'Role-based permissions',
    ],
    limits: { transactions: -1, budgetCategories: -1, receipts: 500, splitBills: -1 },
  },
  {
    id: 'business',
    name: 'Business',
    price: '€24.99',
    period: '/month',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)',
    icon: Sparkles,
    description: 'Enterprise-grade financial management',
    features: [
      'Everything in Family',
      'Up to 15 accounts',
      'Unlimited receipts',
      'Business analytics',
      'API access',
      'Dedicated support',
    ],
    limits: { transactions: -1, budgetCategories: -1, receipts: -1, splitBills: -1 },
  },
];

const PLAN_RANK = { free: 0, standard: 1, premium: 2, family: 3, business: 4 };

const RESOURCE_LABELS = {
  transactions: { label: 'Transactions', icon: TrendingUp, color: '#3b82f6' },
  budgetCategories: { label: 'Budget Categories', icon: Layers, color: '#10b981' },
  receipts: { label: 'Receipts', icon: ReceiptText, color: '#f59e0b' },
  splitBills: { label: 'Split Bills', icon: Users, color: '#8b5cf6' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

const pct = (used, limit) => {
  if (limit === -1 || limit === 0) return 0;
  return Math.min(Math.round((used / limit) * 100), 100);
};

const barColor = (p) => (p < 60 ? '#10b981' : p < 85 ? '#f59e0b' : '#ef4444');

// ─── Component ────────────────────────────────────────────────────────────────
const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [serviceDown, setServiceDown] = useState(false);

  const [cancelDialog, setCancelDialog] = useState(false);
  const [downgradeDialog, setDowngradeDialog] = useState({ open: false, plan: null });

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const toast = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [subRes, usageRes] = await Promise.all([
        api.get('/subscription/current'),
        api.get('/subscription/usage'),
      ]);
      setSubscription(subRes.data.data?.subscription);
      setUsage(usageRes.data.data);
    } catch (err) {
      if (err.response?.status === 503) setServiceDown(true);
      else toast('Failed to load subscription data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleUpgrade = async (planId) => {
    try {
      setActionLoading(true);
      const res = await api.post('/subscription/create-checkout-session', {
        planId,
        billingCycle: 'monthly',
      });
      window.location.href = res.data.data.sessionUrl;
    } catch (err) {
      const serverMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to start checkout';
      console.error('Checkout error detail:', err.response?.data);
      toast(serverMsg, 'error');
      setActionLoading(false);
    }
  };

  const handleDowngrade = async () => {
    try {
      setActionLoading(true);
      await api.post('/subscription/change-plan', { newPlanId: downgradeDialog.plan.id });
      setDowngradeDialog({ open: false, plan: null });
      await fetchData();
      toast('Plan changed successfully');
    } catch (err) {
      toast('Failed to change plan', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setActionLoading(true);
      await api.post('/subscription/cancel');
      setCancelDialog(false);
      await fetchData();
      toast('Subscription cancelled. You have been moved to the Free plan.');
    } catch (err) {
      const data = err.response?.data;
      console.error('Cancel error:', data);
      toast(`Failed to cancel: ${data?.error || data?.message || 'Unknown error'}${data?.step ? ` [${data.step}]` : ''}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      await api.post('/subscription/reactivate');
      await fetchData();
      toast('Subscription reactivated!');
    } catch (err) {
      toast('Failed to reactivate', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const currentPlanMeta = PLANS.find((p) => p.id === subscription?.plan) || PLANS[0];
  const currentRank = PLAN_RANK[subscription?.plan] ?? 0;
  const upgradePlans = PLANS.filter((p) => PLAN_RANK[p.id] > currentRank);
  const downgradePlans = PLANS.filter((p) => PLAN_RANK[p.id] < currentRank && p.id !== 'free');

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} thickness={3} sx={{ color: '#10b981' }} />
          <Typography color="text.secondary">Loading your subscription…</Typography>
        </Stack>
      </Box>
    );
  }

  // ── Service down ───────────────────────────────────────────────────────────
  if (serviceDown) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Box sx={{ fontSize: 64, mb: 2 }}>🔧</Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Subscription Services Temporarily Unavailable
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Our payment infrastructure is undergoing maintenance. Core app features are still available.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')}
          sx={{ background: 'linear-gradient(135deg,#10b981,#3b82f6)', borderRadius: 3 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const PlanIcon = currentPlanMeta.icon;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: (t) => t.palette.mode === 'dark'
        ? 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)'
        : 'linear-gradient(135deg,#f0fdf4 0%,#eff6ff 100%)',
      pb: 8,
    }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 } }}>

        {/* ── Page title ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Box>
            <Button
              onClick={() => navigate('/dashboard')}
              size="small"
              sx={{
                mb: 0.5, color: 'text.secondary', fontWeight: 600,
                fontSize: '0.78rem', px: 1, borderRadius: 2,
                '&:hover': { color: '#7c3aed', background: 'rgba(124,58,237,0.08)' },
              }}
              startIcon={<span style={{ fontSize: 14 }}>←</span>}
            >
              Dashboard
            </Button>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
              Subscription
            </Typography>
            <Typography color="text.secondary">
              Manage your plan, usage, and billing
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <Button size="small" onClick={fetchData} sx={{ minWidth: 0, p: 1, borderRadius: 2 }}>
              <RefreshCw size={18} />
            </Button>
          </Tooltip>
        </Stack>

        {/* ═══════════════════════════════════════════════════════════════════
            HERO — Current Plan Card
        ═══════════════════════════════════════════════════════════════════ */}
        <Card elevation={0} sx={{
          mb: 4,
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          background: currentPlanMeta.gradient,
          color: '#fff',
          boxShadow: `0 20px 60px ${currentPlanMeta.color}44`,
        }}>
          {/* Decorative blob */}
          <Box sx={{
            position: 'absolute', top: -60, right: -60,
            width: 220, height: 220,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            pointerEvents: 'none',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -40, left: '30%',
            width: 160, height: 160,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            pointerEvents: 'none',
          }} />

          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                {/* Badge */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 2,
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <PlanIcon size={24} color="#fff" />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1 }}>
                      {currentPlanMeta.name} Plan
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {currentPlanMeta.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={subscription?.status?.toUpperCase() || 'ACTIVE'}
                    size="small"
                    sx={{
                      background: subscription?.status === 'active' ? '#22c55e' :
                        subscription?.status === 'trialing' ? '#3b82f6' : '#f59e0b',
                      color: '#fff', fontWeight: 700, fontSize: '0.7rem',
                      ml: 'auto !important',
                    }}
                  />
                </Stack>

                {/* Price */}
                <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 2 }}>
                  <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1 }}>
                    {currentPlanMeta.price}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.75 }}>
                    {currentPlanMeta.period}
                  </Typography>
                </Stack>

                {/* Billing dates */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
                  {subscription?.currentPeriodEnd && (
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {subscription.cancelAtPeriodEnd ? 'Access until' : 'Next billing'}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {fmtDate(subscription.currentPeriodEnd)}
                      </Typography>
                    </Box>
                  )}
                  {subscription?.billingCycle && (
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Billing cycle
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {subscription.billingCycle}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* Cancel warning */}
                {subscription?.status === 'cancelled' && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}
                    icon={<AlertTriangle size={18} />}>
                    Your subscription has been cancelled. You are now on the Free plan.
                  </Alert>
                )}

                {/* Action buttons */}
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  {subscription?.plan !== 'free' && (
                    subscription?.cancelAtPeriodEnd ? (
                      <Button
                        variant="contained"
                        onClick={handleReactivate}
                        disabled={actionLoading}
                        startIcon={<RefreshCw size={16} />}
                        sx={{ background: '#22c55e', '&:hover': { background: '#16a34a' }, borderRadius: 2 }}
                      >
                        Reactivate
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => setCancelDialog(true)}
                        startIcon={<XCircle size={16} />}
                        sx={{
                          borderColor: 'rgba(255,255,255,0.5)',
                          color: '#fff',
                          '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                          borderRadius: 2,
                        }}
                      >
                        Cancel Plan
                      </Button>
                    )
                  )}
                  {upgradePlans.length > 0 && subscription?.plan === 'free' && (
                    <Button
                      variant="contained"
                      onClick={() => document.getElementById('upgrade-section')?.scrollIntoView({ behavior: 'smooth' })}
                      endIcon={<ChevronRight size={16} />}
                      sx={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                        '&:hover': { background: 'rgba(255,255,255,0.3)' }, borderRadius: 2 }}
                    >
                      View Upgrades
                    </Button>
                  )}
                </Stack>
              </Grid>

              {/* Features list (desktop) */}
              <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box sx={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(12px)',
                  borderRadius: 3, p: 2.5,
                }}>
                  <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                    Included in your plan
                  </Typography>
                  {currentPlanMeta.features.map((f, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <CheckCircle2 size={14} color="#86efac" />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>{f}</Typography>
                    </Stack>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════════════
            USAGE METERS
        ═══════════════════════════════════════════════════════════════════ */}
        {usage && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Monthly Usage
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(RESOURCE_LABELS).map(([key, meta]) => {
                const used = usage.usage?.[key] ?? 0;
                const limit = usage.limits?.[key] ?? 0;
                const isUnlimited = limit === -1;
                const percent = pct(used, limit);
                const color = barColor(percent);
                const Icon = meta.icon;

                return (
                  <Grid item xs={12} sm={6} lg={3} key={key}>
                    <Card elevation={0} sx={{
                      borderRadius: 3, p: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: 2,
                          background: `${meta.color}22`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon size={18} color={meta.color} />
                        </Box>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {meta.label}
                        </Typography>
                      </Stack>

                      {isUnlimited ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <InfinityIcon size={20} color={meta.color} />
                          <Typography variant="h6" fontWeight={700} sx={{ color: meta.color }}>
                            Unlimited
                          </Typography>
                        </Stack>
                      ) : (
                        <>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                            <Typography variant="h6" fontWeight={800}>{used}</Typography>
                            <Typography color="text.secondary" variant="body2" alignSelf="flex-end">
                              / {limit}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              height: 6, borderRadius: 3,
                              backgroundColor: 'action.hover',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: color,
                                borderRadius: 3,
                              },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {percent}% used
                            {percent >= 85 && (
                              <Chip label="Near limit" size="small" sx={{
                                ml: 1, height: 16, fontSize: '0.6rem',
                                background: '#fef3c7', color: '#d97706',
                              }} />
                            )}
                          </Typography>
                        </>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            UPGRADE SECTION
        ═══════════════════════════════════════════════════════════════════ */}
        {upgradePlans.length > 0 && (
          <Box id="upgrade-section" sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Upgrade Your Plan</Typography>
                <Typography variant="body2" color="text.secondary">
                  Unlock more features. Cancel anytime.
                </Typography>
              </Box>
              <Button size="small" onClick={() => navigate('/subscription/plans')}
                endIcon={<ChevronRight size={14} />} sx={{ color: 'text.secondary' }}>
                Compare all plans
              </Button>
            </Stack>

            <Grid container spacing={2}>
              {upgradePlans.map((plan) => {
                const Icon = plan.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    <Card elevation={0} sx={{
                      borderRadius: 3, overflow: 'hidden',
                      border: '1px solid',
                      borderColor: plan.highlighted ? plan.color : 'divider',
                      position: 'relative',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 30px ${plan.color}33` },
                      ...(plan.highlighted && { boxShadow: `0 8px 30px ${plan.color}44` }),
                    }}>
                      {plan.highlighted && (
                        <Box sx={{
                          background: plan.gradient,
                          color: '#fff', py: 0.5,
                          textAlign: 'center',
                          fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1,
                          textTransform: 'uppercase',
                        }}>
                          ⚡ Most Popular
                        </Box>
                      )}
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                          <Box sx={{
                            width: 40, height: 40, borderRadius: 2,
                            background: `${plan.color}22`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Icon size={20} color={plan.color} />
                          </Box>
                          <Box>
                            <Typography fontWeight={700}>{plan.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{plan.description}</Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 2 }}>
                          <Typography variant="h5" fontWeight={800} sx={{ color: plan.color }}>
                            {plan.price}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{plan.period}</Typography>
                        </Stack>

                        {plan.features.slice(0, 4).map((f, i) => (
                          <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.75 }}>
                            <Check size={14} color={plan.color} style={{ marginTop: 2, flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">{f}</Typography>
                          </Stack>
                        ))}
                        {plan.features.length > 4 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            +{plan.features.length - 4} more features
                          </Typography>
                        )}

                        <Button
                          fullWidth variant="contained"
                          onClick={() => handleUpgrade(plan.id)}
                          disabled={actionLoading}
                          endIcon={actionLoading ? null : <Zap size={16} />}
                          sx={{
                            mt: 2.5, borderRadius: 2,
                            background: plan.gradient,
                            boxShadow: `0 4px 15px ${plan.color}44`,
                            '&:hover': { boxShadow: `0 8px 25px ${plan.color}66` },
                          }}
                        >
                          {actionLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Upgrade to ${plan.name}`}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            DOWNGRADE SECTION (subtle)
        ═══════════════════════════════════════════════════════════════════ */}
        {downgradePlans.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Looking to downgrade?
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {downgradePlans.map((plan) => (
                <Chip
                  key={plan.id}
                  label={`Switch to ${plan.name} (${plan.price}/mo)`}
                  variant="outlined"
                  clickable
                  onClick={() => setDowngradeDialog({ open: true, plan })}
                  sx={{ borderColor: 'divider', color: 'text.secondary' }}
                />
              ))}
              <Chip
                label="Switch to Free"
                variant="outlined"
                clickable
                onClick={() => setDowngradeDialog({ open: true, plan: PLANS[0] })}
                sx={{ borderColor: 'divider', color: 'text.secondary' }}
              />
            </Stack>
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            ACCOUNT INFO FOOTER
        ═══════════════════════════════════════════════════════════════════ */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <CreditCard size={20} color="#64748b" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>Billing managed by Stripe</Typography>
              <Typography variant="caption" color="text.secondary">
                Payments are secure and encrypted. You can cancel anytime from this page.
              </Typography>
            </Box>
            {user?.email && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {user.email}
              </Typography>
            )}
          </Stack>
        </Card>
      </Container>

      {/* ═══════════════════════════════════════════════════════════════════
          CANCEL DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ mb: 2, borderRadius: 2 }}>
            Your account will immediately revert to the Free plan.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This action is immediate. You can reactivate a paid plan at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setCancelDialog(false)} sx={{ borderRadius: 2 }}>
            Keep My Plan
          </Button>
          <Button onClick={handleCancel} color="error" variant="contained"
            disabled={actionLoading} sx={{ borderRadius: 2 }}>
            {actionLoading ? <CircularProgress size={18} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
          DOWNGRADE DIALOG
      ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={downgradeDialog.open} onClose={() => setDowngradeDialog({ open: false, plan: null })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Switch to {downgradeDialog.plan?.name}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You'll move from <strong>{currentPlanMeta.name}</strong> to{' '}
            <strong>{downgradeDialog.plan?.name}</strong>.
          </Typography>
          {currentPlanMeta.features
            .filter((f) => !downgradeDialog.plan?.features?.includes(f))
            .map((f, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <X size={14} color="#ef4444" />
                <Typography variant="body2" color="text.secondary">{f} — will be lost</Typography>
              </Stack>
            ))}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setDowngradeDialog({ open: false, plan: null })} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleDowngrade} variant="contained" color="warning"
            disabled={actionLoading} sx={{ borderRadius: 2 }}>
            {actionLoading ? <CircularProgress size={18} /> : `Switch to ${downgradeDialog.plan?.name}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Toast ─── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2, minWidth: 280 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionManagement;
